import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';
import {
  Action,
  IInvitation,
  IMemberPermissions,
  IMyPermissions,
  IOtherProjectMembership,
  IPermissionItem,
  IProjectMember,
  IRolePreset,
  IRolesCatalog,
  MembershipStatus,
  ProjectRole,
} from '../models/access.models';

/**
 * HTTP-клиент ролевой модели (F11). Единая точка обращения к `/api/access/...`.
 * Авторизация навешивается глобальным AuthInterceptor (заголовок Authorization из cookie).
 */
@Injectable({ providedIn: 'root' })
export class AccessApiService {
  private readonly _base = `${environment.apiUrl}/access`;

  constructor(private readonly _http: HttpClient) {}

  // ─────────────────────── Permissions (F1) ───────────────────────

  /** Матрица эффективных прав текущего пользователя в проекте. */
  getMyPermissions(projectId: number): Observable<IMyPermissions> {
    return this._http.get<IMyPermissions>(`${this._base}/projects/${projectId}/my-permissions/`);
  }

  // ─────────────────────── Members (B5) ───────────────────────

  getMembers(projectId: number): Observable<IProjectMember[]> {
    return this._http.get<IProjectMember[]>(`${this._base}/projects/${projectId}/members/`);
  }

  inviteMember(
    projectId: number,
    body: { email: string; name?: string; role: ProjectRole; preset?: number | null },
  ): Observable<IProjectMember | IInvitation> {
    return this._http.post<IProjectMember | IInvitation>(
      `${this._base}/projects/${projectId}/members/invite/`,
      body,
    );
  }

  updateMember(
    projectId: number,
    mid: number,
    body: { role?: ProjectRole; status?: MembershipStatus; preset?: number | null },
  ): Observable<IProjectMember> {
    return this._http.patch<IProjectMember>(`${this._base}/projects/${projectId}/members/${mid}/`, body);
  }

  deleteMember(projectId: number, mid: number): Observable<void> {
    return this._http.delete<void>(`${this._base}/projects/${projectId}/members/${mid}/`);
  }

  getMemberPermissions(projectId: number, mid: number): Observable<IMemberPermissions> {
    return this._http.get<IMemberPermissions>(
      `${this._base}/projects/${projectId}/members/${mid}/permissions/`,
    );
  }

  /** Полная замена override'ов участника (только guest). */
  putMemberPermissions(
    projectId: number,
    mid: number,
    overrides: IPermissionItem[],
  ): Observable<IMemberPermissions> {
    return this._http.put<IMemberPermissions>(
      `${this._base}/projects/${projectId}/members/${mid}/permissions/`,
      { overrides },
    );
  }

  getOtherProjects(projectId: number, mid: number): Observable<IOtherProjectMembership[]> {
    return this._http.get<IOtherProjectMembership[]>(
      `${this._base}/projects/${projectId}/members/${mid}/other-projects/`,
    );
  }

  // ─────────────────────── Invitations (B6) ───────────────────────

  getMyInvitations(): Observable<IInvitation[]> {
    return this._http.get<IInvitation[]>(`${this._base}/invitations/`);
  }

  acceptInvitation(token: string): Observable<unknown> {
    return this._http.post(`${this._base}/invitations/accept/`, { token });
  }

  /** Принять приглашение прямо в приложении (по id, для авторизованного пользователя).
   * ⚠️ Требует бэкенд-эндпоинт POST /access/invitations/{id}/accept/ (см. ТЗ). */
  acceptInvitationById(id: number): Observable<unknown> {
    return this._http.post(`${this._base}/invitations/${id}/accept/`, {});
  }

  /** Отклонить приглашение (по id). ⚠️ Требует POST /access/invitations/{id}/decline/. */
  declineInvitationById(id: number): Observable<unknown> {
    return this._http.post(`${this._base}/invitations/${id}/decline/`, {});
  }

  getProjectInvitations(projectId: number): Observable<IInvitation[]> {
    return this._http.get<IInvitation[]>(`${this._base}/projects/${projectId}/invitations/`);
  }

  revokeInvitation(projectId: number, iid: number): Observable<void> {
    return this._http.delete<void>(`${this._base}/projects/${projectId}/invitations/${iid}/`);
  }

  resendInvitation(projectId: number, iid: number): Observable<IInvitation> {
    return this._http.post<IInvitation>(
      `${this._base}/projects/${projectId}/invitations/${iid}/resend/`,
      {},
    );
  }

  // ─────────────────────── Transfer ownership (B7) ───────────────────────

  transferOwnership(projectId: number, newOwnerUserId: number): Observable<unknown> {
    return this._http.post(`${this._base}/projects/${projectId}/transfer-ownership/`, {
      new_owner_user_id: newOwnerUserId,
    });
  }

  // ─────────────────────── Role presets (B8) ───────────────────────

  /** Справочник всех ролей и пресетов для «Сравнить все роли» (F8). */
  getRolesCatalog(): Observable<IRolesCatalog> {
    return this._http.get<IRolesCatalog>(`${this._base}/roles/`);
  }

  getRolePresets(): Observable<IRolePreset[]> {
    return this._http.get<IRolePreset[]>(`${this._base}/role-presets/`);
  }

  createRolePreset(body: { name: string; description?: string }): Observable<IRolePreset> {
    return this._http.post<IRolePreset>(`${this._base}/role-presets/`, body);
  }

  updateRolePreset(
    pid: number,
    body: { name?: string; description?: string; permissions?: IPermissionItem[] },
  ): Observable<IRolePreset> {
    return this._http.put<IRolePreset>(`${this._base}/role-presets/${pid}/`, body);
  }

  deleteRolePreset(pid: number): Observable<void> {
    return this._http.delete<void>(`${this._base}/role-presets/${pid}/`);
  }
}
